import { ChangeEvent, useRef, useState } from "react";
import { FieldPath, FieldValues, UseControllerProps, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EARLY_ISSUES, InputKind, finalizeInput, inputIssue, toText } from "../utils/secureInput";
import { issueMessage } from "../utils/validation";

export type SecureInputOptions<
  T extends FieldValues,
  N extends FieldPath<T> = FieldPath<T>
> = UseControllerProps<T, N> & {
  kind?: InputKind;
  maxLength?: number;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
};

/**
 * Wires a react-hook-form field to AuthField/TextField props.
 *
 * Typing is never rewritten. The value is normalised and trimmed on blur (the
 * schema's secureString does the same on submit), and control characters,
 * markup and over-long text are flagged as soon as they are pasted or the field
 * is left, rather than waiting for submit. Passwords pass through untouched.
 */
export const useSecureInput = <T extends FieldValues, N extends FieldPath<T> = FieldPath<T>>({
  kind = "text",
  maxLength,
  onChange,
  onBlur,
  ...controller
}: SecureInputOptions<T, N>) => {
  const { t } = useTranslation();
  const { field, fieldState } = useController(controller);
  const [earlyError, setEarlyError] = useState<string>();
  const pasted = useRef(false);
  const value = toText(field.value);

  const check = (next: string) => {
    const issue = inputIssue(next, { kind, maxLength });
    setEarlyError(issue && EARLY_ISSUES.includes(issue) ? issueMessage(t, issue, { maxLength }) : undefined);
  };

  const helperText = fieldState.error?.message ?? earlyError;

  return {
    name: field.name,
    inputRef: field.ref,
    value,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = event.target.value;
      if (pasted.current) check(next);
      else setEarlyError(undefined);
      pasted.current = false;
      field.onChange(next);
      onChange?.(next);
    },
    onPaste: () => {
      pasted.current = true;
    },
    onBlur: () => {
      const next = finalizeInput(value, kind);
      if (next !== value) field.onChange(next);
      field.onBlur();
      check(next);
      onBlur?.(next);
    },
    error: !!helperText,
    helperText,
  };
};
